import { Leaf, Zap, ShieldCheck, MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { HERO_IMAGE, LOGO_MARK } from "../../data/promill";

const BENEFITS = [
    { icon: Zap, title: "High in Protein", sub: "Fuel your day" },
    { icon: Leaf, title: "Millet Powered", sub: "Better grains, better you" },
    { icon: ShieldCheck, title: "Clean & Fresh", sub: "No compromises" },
];

export const Hero = () => (
    <section
        id="home"
        data-testid="hero-section"
        className="relative bg-pm-cream overflow-hidden"
    >
        {/* Soft brand glow */}
        <div aria-hidden className="absolute inset-0 hero-glow pointer-events-none" />
        <div aria-hidden className="absolute -bottom-20 left-10 w-72 h-72 rounded-full bg-pm-greenBright/10 blur-3xl pointer-events-none" />

        {/* Desktop: full-bleed image on right (image's natural cream BG blends with page).
            Mobile: image stacks below text. */}
        <div className="relative max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-12 items-center min-h-[560px] lg:min-h-[640px]">
                {/* Copy column */}
                <div className="lg:col-span-6 px-4 sm:px-6 lg:px-8 pt-10 lg:pt-0 pb-6 lg:pb-0 fade-up relative z-10">
                    <div className="inline-flex items-center gap-2 mb-5">
                        <img src={LOGO_MARK} alt="" className="h-6 w-6" />
                        <span className="text-pm-green font-bold tracking-[0.22em] uppercase text-[11px] md:text-xs">
                            Protein-First · Millet-Proud
                        </span>
                    </div>

                    <h1
                        data-testid="hero-headline"
                        className="font-sans font-extrabold text-pm-black tracking-[-0.02em] leading-[1.02] text-[2.25rem] sm:text-5xl md:text-6xl lg:text-[4.5rem] xl:text-[5rem]"
                    >
                        Protein <span className="text-pm-green">+</span> Millet.
                        <br />
                        <span className="text-pm-yellow">Taste without guilt.</span>
                    </h1>

                    <p className="mt-5 max-w-xl text-base md:text-lg text-pm-black/70 leading-relaxed">
                        Build-your-own millet wraps and protein bowls packed with clean ingredients
                        and bold flavors — engineered for the hustle of urban India.
                    </p>

                    <div className="mt-7 flex flex-wrap gap-3">
                        <Link to="/menu" data-testid="hero-cta-menu" className="btn-brutal btn-brutal--accent">
                            Explore Menu
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link to="/contact" data-testid="hero-cta-waitlist" className="btn-brutal btn-brutal--ghost">
                            <MapPin className="w-4 h-4" />
                            Join Waitlist
                        </Link>
                    </div>

                    <div className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-xl">
                        {BENEFITS.map(({ icon: Icon, title, sub }) => (
                            <div
                                key={title}
                                data-testid={`benefit-pill-${title.toLowerCase().replace(/\s|&/g, "-")}`}
                                className="flex items-center gap-3"
                            >
                                <div className="flex-shrink-0 h-11 w-11 rounded-full bg-pm-yellow/15 border border-pm-yellow/40 flex items-center justify-center">
                                    <Icon className="w-5 h-5 text-pm-green" />
                                </div>
                                <div>
                                    <div className="text-sm font-bold text-pm-black leading-tight">{title}</div>
                                    <div className="text-xs text-pm-black/55 leading-tight">{sub}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Image column */}
                <div className="lg:col-span-6 relative w-full">
                    <div className="relative w-full">
                        <img
                            src={HERO_IMAGE}
                            alt="ProMill millet wraps and protein bowls"
                            data-testid="hero-image"
                            className="w-full h-auto object-contain"
                        />
                        {/* Sticker badges */}
                        <div className="hidden md:flex absolute top-[10%] left-[6%] z-20 items-center gap-2 bg-white px-4 py-2 rounded-full border border-pm-black/10 shadow-lg">
                            <span className="h-2 w-2 rounded-full bg-pm-greenBright" />
                            <span className="text-xs font-bold tracking-wider uppercase text-pm-black">30g+ Protein</span>
                        </div>
                        <div className="hidden md:flex absolute bottom-[18%] left-[10%] z-20 items-center gap-2 bg-white px-4 py-2 rounded-full border border-pm-black/10 shadow-lg">
                            <span className="h-2 w-2 rounded-full bg-pm-orange" />
                            <span className="text-xs font-bold tracking-wider uppercase text-pm-black">Clean Ingredients</span>
                        </div>
                        <div className="hidden lg:flex absolute top-[42%] right-[6%] z-20 items-center gap-2 bg-pm-green text-pm-yellow px-3.5 py-1.5 rounded-full shadow-lg">
                            <Leaf className="w-3.5 h-3.5" />
                            <span className="text-[10px] font-bold tracking-widest uppercase">100% Millet</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
);
